// Página "Actividad" — timeline cronológica del caso del cliente
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ClientLayout } from '@/components/Layout/ClientLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  ChatBubbleLeftIcon,
  PhoneIcon,
  CreditCardIcon,
  PencilSquareIcon,
  CalendarIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { useClienteFichaQuery } from '@/hooks/useClienteExpediente';
import type { TimelineItem, TimelineItemType } from '@/types/clienteFicha';

const TYPE_CONFIG: Record<string, { icon: typeof ClockIcon; color: string; labelKey: string }> = {
  note: { icon: ChatBubbleLeftIcon, color: 'text-blue-500 bg-blue-100 dark:bg-blue-900', labelKey: 'activity.typeNote' },
  call: { icon: PhoneIcon, color: 'text-green-500 bg-green-100 dark:bg-green-900', labelKey: 'activity.typeCall' },
  payment: { icon: CreditCardIcon, color: 'text-purple-500 bg-purple-100 dark:bg-purple-900', labelKey: 'activity.typePayment' },
  payment_completed: { icon: CreditCardIcon, color: 'text-purple-500 bg-purple-100 dark:bg-purple-900', labelKey: 'activity.typePayment' },
  signature: { icon: PencilSquareIcon, color: 'text-indigo-500 bg-indigo-100 dark:bg-indigo-900', labelKey: 'activity.typeSignature' },
  contract_signed: { icon: PencilSquareIcon, color: 'text-indigo-500 bg-indigo-100 dark:bg-indigo-900', labelKey: 'activity.typeSignature' },
  appointment: { icon: CalendarIcon, color: 'text-orange-500 bg-orange-100 dark:bg-orange-900', labelKey: 'activity.typeAppointment' },
  transcription: { icon: ChatBubbleLeftIcon, color: 'text-teal-500 bg-teal-100 dark:bg-teal-900', labelKey: 'activity.typeTranscription' },
};

const ALL_FILTER_TYPES: { value: string; labelKey: string }[] = [
  { value: 'all', labelKey: 'activity.filterAll' },
  { value: 'note', labelKey: 'activity.typeNote' },
  { value: 'call', labelKey: 'activity.typeCall' },
  { value: 'payment', labelKey: 'activity.typePayment' },
  { value: 'signature', labelKey: 'activity.typeSignature' },
  { value: 'appointment', labelKey: 'activity.typeAppointment' },
  { value: 'transcription', labelKey: 'activity.typeTranscription' },
];

// Normalizar tipos del backend que pueden venir como variantes
function normalizeType(type: string): TimelineItemType | string {
  if (type === 'payment_completed') return 'payment';
  if (type === 'contract_signed') return 'signature';
  return type;
}

function TimelineEntry({ item, t }: { item: TimelineItem; t: (key: string) => string }) {
  const config = TYPE_CONFIG[item.type] || { icon: ClockIcon, color: 'text-gray-500 bg-gray-100 dark:bg-gray-800', labelKey: '' };
  const Icon = config.icon;
  const label = config.labelKey ? t(config.labelKey) : item.type;

  return (
    <div className="flex gap-4">
      <div className={`flex-shrink-0 flex items-center justify-center h-10 w-10 rounded-full ${config.color}`} aria-hidden="true">
        <Icon className="h-5 w-5" />
      </div>
      <div className="flex-1 min-w-0 pb-6 border-b border-gray-100 dark:border-gray-800 last:border-0">
        <div className="flex items-center gap-2 mb-0.5">
          <Badge variant="outline" className="text-xs">{label}</Badge>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {new Date(item.timestamp).toLocaleString('es-ES', {
              day: 'numeric',
              month: 'short',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
        </div>
        {item.title && (
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{item.title}</p>
        )}
        {item.description && (
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">{item.description}</p>
        )}
      </div>
    </div>
  );
}

export function ClientesActividad() {
  const { t } = useTranslation('portal');
  const { data: ficha, isLoading, error: queryError } = useClienteFichaQuery();
  const [filterType, setFilterType] = useState('all');
  const timeline = ficha?.timeline ?? [];

  // Filtrar y ordenar
  const sorted = useMemo(() => {
    const filtered = filterType === 'all'
      ? timeline
      : timeline.filter(item => normalizeType(item.type) === filterType);
    return [...filtered].sort(
      (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );
  }, [timeline, filterType]);

  return (
    <ClientLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{t('activity.title')}</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            {t('activity.subtitle')}
          </p>
        </div>

        {/* Filtros */}
        {timeline.length > 0 && (
          <div className="flex flex-wrap gap-2" role="group" aria-label={t('activity.filterLabel', 'Filtrar actividad')}>
            {ALL_FILTER_TYPES.map((f) => (
              <Button
                key={f.value}
                variant={filterType === f.value ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilterType(f.value)}
              >
                {t(f.labelKey, f.value === 'all' ? 'Todas' : f.value)}
              </Button>
            ))}
          </div>
        )}

        {isLoading ? (
          <div className="space-y-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="flex gap-4">
                <div className="h-10 w-10 bg-gray-200 dark:bg-gray-700 rounded-full animate-pulse flex-shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-32 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                  <div className="h-3 w-48 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                </div>
              </div>
            ))}
          </div>
        ) : queryError ? (
          <Card>
            <CardContent className="pt-6 text-center py-12">
              <p className="text-red-600 dark:text-red-400 mb-4">{t('activity.loadError', 'Error al cargar la actividad.')}</p>
              <Button variant="outline" onClick={() => window.location.reload()}>
                {t('common.retry', 'Reintentar')}
              </Button>
            </CardContent>
          </Card>
        ) : sorted.length === 0 ? (
          <Card>
            <CardContent className="pt-6 text-center py-12">
              <ClockIcon className="h-12 w-12 mx-auto text-gray-400 dark:text-gray-500 mb-3" />
              <p className="text-gray-500 dark:text-gray-400">
                {filterType !== 'all'
                  ? t('activity.noFilteredActivity', 'No hay actividad de este tipo.')
                  : t('activity.noActivity')}
              </p>
              {filterType !== 'all' && (
                <Button variant="ghost" size="sm" className="mt-2" onClick={() => setFilterType('all')}>
                  {t('activity.showAll', 'Ver toda la actividad')}
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="pt-6 space-y-4">
              {sorted.map((item) => (
                <TimelineEntry key={item.id} item={item} t={t} />
              ))}
            </CardContent>
          </Card>
        )}
      </div>
    </ClientLayout>
  );
}
